import { useInfiniteQuery } from "@tanstack/react-query";
import { useAxios } from "../../../api/axios";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import {
  FiltersApplied,
  InvestorSearchExecuted
} from "../../../utils/inv-exch-amplitude";

export const useFlipperVault = () => {
  const axios = useAxios();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  return useInfiniteQuery({
    queryKey: ["flipper-vaults", searchParams.toString()],
    initialPageParam: 1,
    queryFn: async ({ pageParam = 1 }: any) => {
      try {
        const lat = searchParams.get("lat") ?? "33.949553";
        const lng = searchParams.get("lng") ?? "-118.112676";
        const showIEButton = searchParams.get("showIEButton") ?? "true";
        const view =
          searchParams.get("view") === "ALL ACCOUNTS" && showIEButton === "true"
            ? "ALL ACCOUNTS"
            : "MY ACCOUNTS";
        const acctType = searchParams.get("acctType") ?? "FT VERIFIED";
        const pubPropertyId = searchParams.get("pid") ?? "";
        const pubPropertySfid = searchParams.get("sfid") ?? "";
        const searchText = searchParams.get("searchText") ?? "";
        const order = searchParams.get("order") ?? "Low to High";
        const orderEmail = searchParams.get("orderEmail") || "";
        const checkoutStatus = searchParams.get("checkoutStatus") ?? "OFF";
        const clicked = searchParams.get("clicked") ?? false;
        const shouldIgnoreRadius =
          searchParams.get("shouldIgnoreRadius") ?? true;

        if (searchText?.length > 0) {
          InvestorSearchExecuted({
            search_term: searchText,
            property_id: pubPropertyId || "",
            property_sfid: pubPropertySfid
          });
        }
        if (clicked) {
          FiltersApplied({
            property_id: pubPropertyId || "",
            property_sfid: pubPropertySfid,
            filter_values: {
              radius: 100,
              view: view,
              acctType: acctType,
              order: order,
              checkoutStatus: checkoutStatus,
              shouldIgnoreRadius: shouldIgnoreRadius
            }
          });
        }
        const radius = searchParams.get("radius") ?? "100";
        const sortBy =
          orderEmail && orderEmail.length > 0 ? "emailScore" : "interestScore";
        const sortDir =
          (orderEmail && orderEmail.length > 0 ? orderEmail : order) ===
          "Low to High"
            ? "ASC"
            : "DESC";

        const urlKey = `${t(
          "inv-exchange-account-listing"
        )}?page=${pageParam}&pubPropertyId=${pubPropertyId}&limit=20&radius=${radius}&shouldIgnoreRadius=${shouldIgnoreRadius}&longitude=${lng}&latitude=${lat}${
          acctType?.length > 0 ? `&acctType=${acctType}` : ""
        }${view?.length > 0 ? `&view=${view}` : ""}${
          searchText?.length > 0
            ? `&searchTxt=${encodeURIComponent(searchText)}`
            : ""
        }&orderBy=${sortBy}&order=${sortDir}${
          checkoutStatus?.length > 0
            ? `&checkoutStatus=${
                checkoutStatus === "CHECKED OUT"
                  ? "CHECKED OUT ONLY"
                  : checkoutStatus === "AVAILABLE"
                  ? "AVAILABLE ONLY"
                  : "ALL"
              }`
            : ""
        }`;
        const response = await axios.get(urlKey);
        return response.data.data;
      } catch (error) {
        console.error("Error fetching flipper vault data:", error);
        navigate(
          "/properties?lat=32.579632406829035&lng=-116.8282973867345&zoom=6.321072704923566&state=CA&status=Active"
        );
        return {
          accounts: [],
          acctPre: [],
          full_count: 0
        };
      }
    },
    select: (data) => {
      const firstPageAcctPre = data?.pages?.[0]?.acctPre || [];

      return {
        ...data,
        pages: data?.pages?.map((page) => ({
          ...page,
          items: page?.accounts?.map((account: any) => {
            const preAccount = firstPageAcctPre.find(
              (pre: any) => pre.acctId === account.acctId
            );

            // Sort checkouts array by createdAt
            const sortedCheckouts = account.checkouts
              ? [...account.checkouts].sort(
                  (a: any, b: any) =>
                    moment(a.createdAt).valueOf() -
                    moment(b.createdAt).valueOf()
                )
              : [];

            if (preAccount) {
              return {
                ...account,
                checkouts: sortedCheckouts,
                totalPurchased: preAccount?.totalPurchased,
                totalPurchasedFT: preAccount?.totalPurchasedFT,
                totalSold: preAccount?.totalSold,
                totalFlips: preAccount?.totalFlips,
                lastPurchased: preAccount.lastPurchased,
                lastSold: preAccount?.lastSold,
                pre: preAccount?.pre
              };
            } else {
              return {
                ...account,
                checkouts: sortedCheckouts,
                totalPurchased: "0",
                totalPurchasedFT: "0",
                totalSold: "0",
                totalFlips: "0",
                lastPurchased: null,
                lastSold: null,
                pre: []
              };
            }
          })
        }))
      };
    },
    placeholderData: (previousData) => previousData, // Identity function
    getNextPageParam: (lastPage: any, allPages: any) => {
      if (!lastPage.accounts || lastPage.accounts.length === 0) {
        return undefined;
      }
      return allPages?.length + 1;
    }
  });
};
